import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Copy, Check, KeyRound, ArrowRight, Terminal, Book, Globe } from "lucide-react";
import { toast } from "sonner";

function CodeBlock({ code, testid }) {
    const [copied, setCopied] = useState(false);
    function copy() {
        navigator.clipboard.writeText(code);
        setCopied(true);
        toast.success("Copied");
        setTimeout(() => setCopied(false), 2000);
    }
    return (
        <div className="relative group" data-testid={testid}>
            <pre className="font-mono text-xs bg-[#1f1b16] text-[#f5e9d7] rounded-xl p-5 overflow-x-auto leading-relaxed whitespace-pre">{code}</pre>
            <button
                onClick={copy}
                className="absolute top-3 right-3 opacity-70 hover:opacity-100 rounded-md bg-white/10 hover:bg-white/20 text-white text-[10px] px-2 py-1 inline-flex items-center gap-1"
                aria-label="Copy code"
            >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
            </button>
        </div>
    );
}

function Endpoint({ method, path, blurb, auth, code, testid }) {
    const methodColor = {
        GET: "bg-[#e8f0eb] text-[#2f6544]",
        POST: "bg-[#fdece5] text-[#8b3820]",
        PATCH: "bg-[#fff5db] text-[#8a6a12]",
        DELETE: "bg-[#fce5e5] text-[#8b1c1c]",
    }[method] || "bg-[var(--rl-cream)] text-[var(--rl-text)]";
    return (
        <div className="card-warm rounded-2xl p-6 lg:p-7" data-testid={testid}>
            <div className="flex items-baseline gap-3 flex-wrap">
                <span className={`text-[11px] font-mono font-bold uppercase px-2 py-1 rounded ${methodColor}`}>{method}</span>
                <code className="font-mono text-sm text-[var(--rl-text)]">{path}</code>
                {auth && (
                    <span className="text-[10px] uppercase tracking-widest text-[var(--rl-text-3)] inline-flex items-center gap-1">
                        <KeyRound className="w-3 h-3" /> {auth}
                    </span>
                )}
            </div>
                        <p className="mt-3 text-sm text-[var(--rl-text-2)] leading-relaxed">{blurb}</p>
            {code && <div className="mt-4"><CodeBlock code={code} /></div>}
        </div>
    );
}

export default function ApiDocs() {
    return (
        <div data-testid="api-docs-page">
            {/* Hero */}
            <section className="bg-[#1f1b16] text-[#f5e9d7]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <div className="eyebrow mb-4 inline-flex items-center gap-2 text-[#f2b787]">
                            <Terminal className="w-3.5 h-3.5" /> Developer docs · v1
                        </div>
                        <h1 className="font-serif text-[42px] leading-[1.05] sm:text-6xl tracking-tight">
                            One REST API,<br />
                            <em className="not-italic text-[#f2b787]">every rescue on the network.</em>
                        </h1>
                        <p className="mt-6 text-lg opacity-85 max-w-xl leading-relaxed">
                            Sync your rescue&apos;s dogs, current needs, and available resources programmatically. Use it with Airtable, Zapier, or any backend you already run.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/join"><Button className="rounded-full h-11 px-5 bg-[#f2b787] text-[#1f1b16] hover:bg-[#e6a670]">Get an API key <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
                            <a href="#endpoints"><Button variant="outline" className="rounded-full h-11 px-5 border-white/20 text-[#f5e9d7] hover:bg-white/10 hover:text-white">Jump to endpoints</Button></a>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-xl bg-white/5 border border-white/10 p-5 text-sm">
                            <div className="eyebrow text-[#f2b787] mb-2 inline-flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Base URL</div>
                            <code className="font-mono text-[13px] break-all">https://rescuelink.earth/api</code>
                            <div className="eyebrow text-[#f2b787] mt-5 mb-2 inline-flex items-center gap-2"><KeyRound className="w-3.5 h-3.5" /> Auth</div>
                            <code className="font-mono text-[13px] break-all">X-API-Key: rl_...</code>
                            <div className="text-xs opacity-70 mt-2">Also accepts <code className="font-mono">Authorization: Bearer &lt;jwt&gt;</code></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quickstart */}
            <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
                <div className="eyebrow text-[var(--rl-primary)] mb-3 inline-flex items-center gap-2"><Book className="w-3.5 h-3.5" /> Quickstart</div>
                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Register once. Sync forever.</h2>
                <div className="mt-8 grid lg:grid-cols-2 gap-6">
                    <div>
                        <div className="eyebrow mb-2">1. Register your rescue</div>
                        <p className="text-sm text-[var(--rl-text-2)] mb-3">Public endpoint. Returns a JWT + API key. Store the key — it&apos;s only shown once.</p>
                        <CodeBlock testid="code-register" code={`curl -X POST https://rescuelink.earth/api/rescues/register \\
  -H "Content-Type: application/json" \\
  -d '{
    "org_name": "Second Chance Nashville",
    "email": "team@example.org",
    "password": "correct-horse-battery",
    "contact_person": "Ada Lovelace",
    "location": "Nashville, TN",
    "country": "USA",
    "animal_types": ["dogs", "puppies"],
    "current_needs": ["food", "fosters"],
    "resources_offered": ["transport"]
  }'`} />
                    </div>
                    <div>
                        <div className="eyebrow mb-2">2. Post a current need</div>
                        <p className="text-sm text-[var(--rl-text-2)] mb-3">Uses your API key. Appears in the public <code className="font-mono">GET /needs</code> feed instantly.</p>
                        <CodeBlock testid="code-need" code={`curl -X POST https://rescuelink.earth/api/rescues/me/needs \\
  -H "X-API-Key: rl_YOURKEYHERE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "category": "food",
    "urgency": "high",
    "title": "150 lbs puppy food for orphan litter",
    "description": "Litter of 12 orphans arrived yesterday.",
    "quantity": "150 lbs"
  }'`} />
                    </div>
                </div>
            </section>

            {/* Endpoints */}
            <section id="endpoints" className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-16">
                <div className="eyebrow text-[var(--rl-primary)] mb-3">Endpoints</div>
                <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-8">Six endpoints. That&apos;s the whole surface.</h2>
                <div className="grid gap-4" data-testid="endpoints-list">
                    <Endpoint testid="ep-register"
                        method="POST" path="/api/rescues/register" auth="public"
                        blurb="Create a rescue account. Returns { token, user, rescue, api_key }. The api_key is shown ONCE."
                    />
                    <Endpoint testid="ep-me"
                        method="GET" path="/api/rescues/me" auth="X-API-Key or JWT"
                        blurb="Fetch your rescue profile including animal_types, current_needs, resources_offered, verification status, and social links."
                    />
                    <Endpoint testid="ep-patch"
                        method="PATCH" path="/api/rescues/me" auth="X-API-Key or JWT"
                        blurb="Update any field on your rescue profile. Only send the fields you want to change."
                        code={`curl -X PATCH https://rescuelink.earth/api/rescues/me \\
  -H "X-API-Key: rl_YOURKEY" \\
  -H "Content-Type: application/json" \\
  -d '{"current_needs":["food","medical"],"donation_url":"https://give.example.org"}'`}
                    />
                    <Endpoint testid="ep-rotate"
                        method="POST" path="/api/rescues/me/api-key/rotate" auth="X-API-Key or JWT"
                        blurb="Rotate your API key. Returns { api_key, prefix }. The old key is immediately revoked."
                    />
                    <Endpoint testid="ep-needs"
                        method="POST" path="/api/rescues/me/needs" auth="X-API-Key or JWT"
                        blurb="Publish a current need to the public /needs feed. Categories: food, volunteers, fosters, funds, supplies, medical, transport, other. Urgency: low | medium | high | critical."
                    />
                    <Endpoint testid="ep-resources"
                        method="POST" path="/api/rescues/me/resources" auth="X-API-Key or JWT"
                        blurb="Publish a resource you can offer other rescues (spare transport seats, foster availability, vet hours, etc.). Appears on the public /rescue-resources feed."
                    />
                    <Endpoint testid="ep-list-rescues"
                        method="GET" path="/api/rescues?country=&region=&q=" auth="public"
                        blurb="List all rescues. Optional filters: country, region, state, q (fuzzy on name + description)."
                    />
                    <Endpoint testid="ep-list-needs"
                        method="GET" path="/api/needs?urgency=&category=" auth="public"
                        blurb="Public feed of open needs across the network. Sorted newest-first."
                    />
                </div>
            </section>

            {/* Errors + versioning */}
            <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-20 grid md:grid-cols-2 gap-6">
                <div className="card-warm rounded-2xl p-6">
                    <h3 className="font-serif text-2xl">Errors</h3>
                    <p className="text-sm text-[var(--rl-text-2)] mt-2 leading-relaxed">
                        <strong>400</strong> · validation · <strong>401</strong> · missing / bad auth · <strong>403</strong> · role/ownership · <strong>404</strong> · not found · <strong>422</strong> · schema violation · <strong>5xx</strong> · we broke something — retry with backoff.
                    </p>
                </div>
                <div className="card-warm rounded-2xl p-6">
                    <h3 className="font-serif text-2xl">Rate limits &amp; SLA</h3>
                    <p className="text-sm text-[var(--rl-text-2)] mt-2 leading-relaxed">
                        Fair-use: 60 req/min per key. Higher limits for verified rescues on request. Uptime: 99.9%. Status: <code className="font-mono">GET /api/</code>.
                    </p>
                </div>
            </section>
        </div>
    );
}
